import React from "react";
import Logo from "../assets/logo-applaby.png";
import { useTranslation } from "react-i18next";

import { Helmet } from "react-helmet-async";

export default function PrivacyPage() {
	const { t } = useTranslation();
	return (
		<>
			<Helmet>
				<title>{t("seo.privacy.title")}</title>
				<meta name="description" content={t("seo.privacy.description")} />
				<meta name="robots" content="noindex, follow" />
			</Helmet>
			<div className="bg-gray-50 min-h-screen py-12">
				<section className="max-w-4xl mx-auto bg-white shadow-md rounded-xl p-8">
					<a
						href={"/"}
						className="mb-10"
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<img
							src={Logo}
							alt="Logo Applaby"
							style={{ height: "40px", objectFit: "contain" }}
						/>
					</a>
					<h1 className="text-3xl font-bold text-gray-900 mb-2">
						Política de Privacidad
					</h1>

					<p className="text-sm text-gray-500 mb-8">
						Actualizado el 24 de septiembre de 2025
					</p>

					<p className="text-gray-700 mb-3">
						En Applaby estamos comprometidos con la protección de tu privacidad.
						En esta Política de Privacidad explicamos cómo recopilamos,
						utilizamos y compartimos tu información personal.
					</p>
					<p className="text-gray-700 mb-3">
						Esta Política de Privacidad se aplica a nuestro sitio web, a la
						aplicación Applaby y a los servicios relacionados. Al acceder o
						utilizar cualquiera de ellos, confirmas que has leído, entendido y
						aceptado la recopilación, el uso y la divulgación de tu información
						personal tal como se describe en esta Política de Privacidad y en
						nuestros Términos y Condiciones.
					</p>

					<p className="text-gray-700 mb-3">
						Nuestros servicios están dirigidos exclusivamente a empresas,
						negocios o profesionales. A efectos de esta Política de Privacidad,
						cualquier empresa, negocio o profesional que se registre en la
						plataforma Applaby y contrate nuestros servicios será referido en lo
						sucesivo como el “Negocio” o el “Cliente”.
					</p>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						1. Responsable del tratamiento
					</h2>
					<p className="text-gray-700 mb-3">
						El responsable del tratamiento de tus datos es Yowlu SL, con
						domicilio en Barcelona (España). Puedes ponerte en contacto en{" "}
						<a
							href="mailto:info@applaby.com"
							className="text-green-600 hover:underline"
						>
							info@applaby.com
						</a>
						.
					</p>
					<p className="text-gray-700 mb-3">
						El tratamiento de los datos se realiza conforme al Reglamento (UE)
						2016/679, General de Protección de Datos (RGPD) y a la Ley Orgánica
						3/2018 de Protección de Datos Personales y garantía de los derechos
						digitales (LOPDGDD) .
					</p>
					<p className="text-gray-700 mb-3">
						En relación con los datos de los usuarios finales gestionados por
						los negocios a través de la plataforma Applaby, Yowlu SL actúa en
						calidad de Encargado del Tratamiento, mientras que el negocio que
						contrata los servicios será el Responsable del Tratamiento. La
						aceptación de los Términos y Condiciones y de esta Política de
						Privacidad implica la formalización del correspondiente contrato de
						encargo de tratamiento (DPA) conforme al artículo 28 RGPD.
					</p>
					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						2. Datos que recopilamos
					</h2>
					<p className="text-gray-700 mb-3">
						Podemos recopilar los siguientes datos:
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
						<li>Datos de identificación (nombre, apellidos, empresa).</li>
						<li>Datos de contacto (email, teléfono).</li>
						<li>
							Datos de facturación (NIF, dirección, método de pago a través de
							Stripe).
						</li>
						<li>Datos técnicos (IP, dispositivo, navegador).</li>
						<li>
							Datos de uso de la plataforma (acciones realizadas, reservas,
							pedidos, etc.).
						</li>
						<li>
							Datos de pago procesados de forma segura a través de Stripe (no
							almacenamos los datos completos de la tarjeta).
						</li>
					</ul>
					<p className="text-gray-700 mb-3">
						En el caso de los usuarios finales de las apps o webs creadas con
						Applaby, tratamos los datos en calidad de encargados del
						tratamiento, siguiendo las instrucciones del negocio titular.
					</p>

					<p className="text-gray-700 mb-3">
						Nuestros servicios están dirigidos exclusivamente a empresas y
						profesionales. No recopilamos intencionadamente datos de menores de
						14 años. Si detectamos que se han proporcionado datos de un menor
						sin consentimiento parental, procederemos a eliminarlos de forma
						inmediata.
					</p>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						3. Finalidad del tratamiento
					</h2>
					<ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
						<li>
							Prestar el servicio de creación y gestión de apps y webs mediante
							Applaby.
						</li>
						<li>Gestionar pagos de suscripciones y facturación.</li>
						<li>Ofrecer soporte técnico y atención al cliente.</li>
						<li>
							Enviar comunicaciones relacionadas con el servicio,
							actualizaciones técnicas o notificaciones importantes necesarias
							para la prestación del servicio. Estos correos tienen carácter
							transaccional.
						</li>
						<li>
							Garantizar la seguridad y correcto funcionamiento de la
							plataforma.
						</li>
						<li>
							Actualmente no enviamos comunicaciones comerciales periódicas
							(newsletters). En caso de que en el futuro se realicen, únicamente
							se enviarán con tu consentimiento previo y podrás darte de baja en
							cualquier momento.
						</li>
					</ul>
					<p>
						También podemos usar tu información para informarte sobre
						promociones o novedades de la plataforma, siempre con tu
						consentimiento.
					</p>
					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						4. Base legal
					</h2>
					<p className="text-gray-700 mb-3">
						Tratamos tus datos personales conforme a las siguientes bases
						jurídicas:
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
						<li>
							<strong>Ejecución de un contrato:</strong> cuando el tratamiento
							es necesario para gestionar tu registro en la plataforma, la
							prestación del servicio contratado, la gestión de pagos y
							facturación o el envío de comunicaciones relacionadas con la
							plataforma.
						</li>
						<li>
							<strong>Cumplimiento de obligaciones legales:</strong> cuando el
							tratamiento es necesario para cumplir con la normativa vigente
							(por ejemplo, obligaciones fiscales y contables).
						</li>
						<li>
							<strong>Consentimiento:</strong> cuando aceptas de forma expresa
							el uso de cookies no esenciales o herramientas de analítica.
							Puedes retirar tu consentimiento en cualquier momento a través del
							banner de cookies o la configuración de tu navegador.
						</li>
						<li>
							<strong>Interés legítimo:</strong> cuando tratamos datos para
							mejorar la seguridad y usabilidad de la plataforma, prevenir
							fraudes o realizar pequeñas mejoras en el servicio, siempre que no
							prevalezcan tus derechos e intereses.
						</li>
					</ul>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						5. Destinatarios de los datos
					</h2>
					<p className="text-gray-700 mb-3">
						Compartimos datos solo con proveedores externos de confianza y
						estrictamente necesarios para la correcta prestación del servicio
						(por ejemplo, servicios de hosting, correo electrónico, analítica o
						procesamiento de pagos como Stripe).
					</p>

					<ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
						<li>
							Stripe, para pagos seguros. Stripe cumple con los estándares
							internacionales de seguridad PCI-DSS.
						</li>
						<li>Proveedores de hosting y almacenamiento en la UE.</li>
						<li>
							Herramientas de analítica o soporte (ej. Hotjar, solo con tu
							consentimiento).
						</li>
					</ul>
					<p className="text-gray-700 mb-3">
						Algunos de estos proveedores pueden estar fuera de la UE; en esos
						casos, se aplican las Cláusulas Contractuales Tipo de la Comisión
						Europea para garantizar un nivel adecuado de protección.
					</p>

					<p className="text-gray-700 mb-3">
						Para determinadas funcionalidades (como la autocompletación de
						direcciones o la visualización de mapas), utilizamos la API de
						Google Maps. Esto implica que, al utilizar estas funciones, Google
						puede recopilar datos técnicos del usuario (como la dirección IP o
						la ubicación aproximada), de acuerdo con su propia{" "}
						<a
							href="https://policies.google.com/privacy"
							target="_blank"
							className="text-green-600 hover:underline"
							rel="noreferrer"
						>
							Política de Privacidad
						</a>
						.
					</p>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						6. Plazo de conservación
					</h2>
					<p className="text-gray-700 mb-3">
						Los datos se conservarán mientras dure la relación contractual y,
						posteriormente, durante el tiempo necesario para cumplir con
						obligaciones legales (ej. facturación). Los datos relacionados con
						analítica o cookies se conservarán según los plazos de la Política
						de Cookies.
					</p>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						7. Derechos de los interesados
					</h2>
					<p className="text-gray-700 mb-3">
						De acuerdo con la normativa vigente en materia de protección de
						datos, y en particular con el Reglamento General de Protección de
						Datos (RGPD), tienes derecho a:
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
						<li>Solicitar el acceso a tus datos personales.</li>
						<li>
							Solicitar la rectificación de datos inexactos o incompletos.
						</li>
						<li>
							Solicitar la supresión de tus datos (“derecho al olvido”) cuando,
							entre otros motivos, los datos ya no sean necesarios para los
							fines para los que fueron recogidos.
						</li>
						<li>
							Solicitar la limitación del tratamiento de tus datos en
							determinadas circunstancias.
						</li>
						<li>
							Oponerte al tratamiento de tus datos, en cuyo caso dejaremos de
							tratarlos salvo por motivos legítimos imperiosos o para el
							ejercicio o defensa de reclamaciones.
						</li>
						<li>
							Solicitar la portabilidad de tus datos a otro responsable, cuando
							sea técnicamente posible.
						</li>
					</ul>
					<p className="text-gray-700 mb-3">
						Para ejercer estos derechos, puedes dirigirte a Applaby enviando una
						solicitud al correo electrónico{" "}
						<a
							href="mailto:info@applaby.com"
							className="text-green-600 hover:underline"
						>
							info@applaby.com
						</a>
						, adjuntando en su caso una copia de tu documento de identidad o
						cualquier otro medio válido en derecho que permita acreditar tu
						identidad.
					</p>
					<p className="text-gray-700 mb-3">
						Asimismo, te informamos de que tienes derecho a presentar una
						reclamación ante la Agencia Española de Protección de Datos (AEPD)
						si consideras que el tratamiento de tus datos infringe la normativa
						aplicable.
					</p>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						8. Cookies
					</h2>
					<p className="text-gray-700 mb-3">
						El uso de cookies se regula en nuestra{" "}
						<a href="/cookies" className="text-green-600 hover:underline">
							Política de Cookies
						</a>
						.
					</p>
					<p>
						No se instalarán cookies no esenciales (por ejemplo, de analítica o
						marketing) sin tu consentimiento previo y expreso, recogido a través
						del banner de cookies de la plataforma, en cumplimiento de las
						directrices de la AEPD.
					</p>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						9. Seguridad
					</h2>
					<p>
						Applaby aplica medidas técnicas y organizativas para proteger los
						datos personales contra pérdida, alteración o acceso no autorizado,
						cumpliendo con lo previsto en el artículo 32 del RGPD. No obstante,
						ningún sistema es 100% seguro y no podemos garantizar la seguridad
						absoluta, pero aplicamos estándares adecuados de seguridad.
					</p>
					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						10. Transferencias internacionales de datos
					</h2>
					<p>
						Cuando sea necesario transferir datos personales fuera del Espacio
						Económico Europeo (EEE), dichas transferencias estarán amparadas por
						una decisión de adecuación de la Comisión Europea o, en su defecto,
						por la firma de Cláusulas Contractuales Tipo aprobadas por la
						Comisión.
					</p>
					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						11. Enlaces a otros sitios web
					</h2>
					<p className="text-gray-700 mb-3">
						Esta Política de Privacidad se aplica solo a los servicios de
						Applaby. Nuestros servicios pueden incluir enlaces a sitios web
						externos o integraciones de terceros (como Stripe o Google). No
						somos responsables del contenido, exactitud o prácticas de
						privacidad de dichos sitios. Te recomendamos revisar sus políticas
						de privacidad al visitarlos.
					</p>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						12. Cambios en esta política
					</h2>
					<p className="text-gray-700 mb-3">
						Podemos actualizar esta Política de Privacidad cuando sea necesario.
						Si los cambios son relevantes, lo notificaremos en la plataforma o
						por email.
					</p>
					<p>
						En caso de venta, fusión o reestructuración de Applaby, los datos
						podrán ser transferidos a un tercero, siempre garantizando el
						cumplimiento de esta Política de Privacidad.
					</p>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						13. Ley aplicable
					</h2>
					<p className="text-gray-700 mb-3">
						Esta Política de Privacidad se rige por la legislación española.
						Cualquier controversia será resuelta en los Juzgados y Tribunales de
						Barcelona.
					</p>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						14. Contacto
					</h2>
					<p className="text-gray-700 mb-3">
						Si tienes dudas o quieres ejercer tus derechos, escríbenos a:
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
						<li>
							Email:{" "}
							<a
								href="mailto:info@applaby.com"
								className="text-green-600 hover:underline"
							>
								info@applaby.com
							</a>
						</li>
					</ul>
				</section>
			</div>
		</>
	);
}
